import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, ArrowLeft, Target, Users, Film, Shield, DollarSign } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const campaignSchema = z.object({
  campaign_name: z.string().min(2, 'Campaign name is required'),
  target_demographics: z.array(z.string()).min(1, 'Select at least one demographic'),
  content_preferences: z.array(z.string()).min(1, 'Select at least one content preference'),
  budget_usd: z.number().min(1000, 'Minimum budget is $1,000'),
  campaign_start_date: z.string().min(1, 'Start date is required'),
  campaign_end_date: z.string().min(1, 'End date is required'),
  target_ecpm: z.number().min(0.1, 'Target eCPM must be greater than $0.10'),
  success_metrics: z.array(z.string()).min(1, 'Select at least one success metric'),
  suitability_requirements: z.array(z.string()).optional(),
});

type CampaignFormValues = z.infer<typeof campaignSchema>;

interface BookingWindow {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
}

const CampaignCreationPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [activeBookingWindow, setActiveBookingWindow] = useState<BookingWindow | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      campaign_name: '',
      target_demographics: [],
      content_preferences: [],
      budget_usd: 50000,
      campaign_start_date: '',
      campaign_end_date: '',
      target_ecpm: 5.00,
      success_metrics: [],
      suitability_requirements: [],
    },
  });

  const demographics = [
    { id: 'gen_z', label: 'Gen Z (18-24)', description: 'Digital natives, social media focused' },
    { id: 'millennials', label: 'Millennials (25-40)', description: 'Tech-savvy, value-driven consumers' },
    { id: 'gen_x', label: 'Gen X (41-56)', description: 'Established professionals, family-focused' },
    { id: 'baby_boomers', label: 'Baby Boomers (57+)', description: 'Traditional media consumers' },
  ];

  const contentPreferences = [
    { id: 'comedy', label: 'Comedy', icon: '😄' },
    { id: 'drama', label: 'Drama', icon: '🎭' },
    { id: 'action', label: 'Action', icon: '⚡' },
    { id: 'thriller', label: 'Thriller', icon: '🔥' },
    { id: 'documentary', label: 'Documentary', icon: '📚' },
    { id: 'reality', label: 'Reality', icon: '📺' },
    { id: 'educational', label: 'Educational', icon: '🎓' },
    { id: 'lifestyle', label: 'Lifestyle', icon: '✨' },
    { id: 'tech', label: 'Tech', icon: '💻' },
    { id: 'gaming', label: 'Gaming', icon: '🎮' },
    { id: 'music', label: 'Music', icon: '🎵' },
    { id: 'sports', label: 'Sports', icon: '⚽' },
  ];

  const successMetrics = [
    { id: 'brand_awareness', label: 'Brand Awareness' },
    { id: 'reach', label: 'Reach & Impressions' },
    { id: 'engagement', label: 'Engagement Rate' },
    { id: 'conversions', label: 'Conversions' },
    { id: 'website_traffic', label: 'Website Traffic' },
    { id: 'lead_generation', label: 'Lead Generation' },
  ];

  const suitabilityOptions = [
    { id: 'no_violence', label: 'No Violence' },
    { id: 'no_political', label: 'No Political Content' },
    { id: 'no_adult', label: 'No Adult Content' },
    { id: 'no_controversial', label: 'No Controversial Topics' },
    { id: 'no_competitor', label: 'No Competitor Mentions' },
    { id: 'family_friendly', label: 'Family Friendly Only' },
  ];

  useEffect(() => {
    fetchActiveBookingWindow();
  }, []);

  const fetchActiveBookingWindow = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/booking-windows/active`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const window = await response.json();
        setActiveBookingWindow(window);
      }
    } catch (error) {
      console.error('Error fetching booking window:', error);
    }
  };

  const onSubmit = async (data: CampaignFormValues) => {
    try {
      setLoading(true);
      
      const campaignData = {
        ...data,
        window_id: activeBookingWindow?.id,
        requirements: {
          budget_usd: data.budget_usd,
          campaign_start_date: new Date(data.campaign_start_date),
          campaign_end_date: new Date(data.campaign_end_date),
          target_audience: data.target_demographics,
          target_categories: data.content_preferences,
          suitability_filters: data.suitability_requirements || [],
          success_metrics: data.success_metrics,
          target_ecpm: data.target_ecpm,
        }
      };

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        throw new Error('Failed to create campaign');
      }

      const campaign = await response.json();
      
      toast({
        title: "Campaign Created Successfully",
        description: "Your campaign requirements have been saved. You can now browse eligible inventory."
      });

      navigate(`/brand-marketplace/browse?campaign_id=${campaign.id}`);
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to create campaign. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleArrayFieldChange = (field: keyof CampaignFormValues, value: string, checked: boolean) => {
    const currentValues = form.getValues(field) as string[];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(item => item !== value);
    form.setValue(field, newValues);
  };

  const progress = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Campaign</h1>
          <p className="text-gray-600">
            Define your campaign requirements to discover eligible inventory
          </p>
          {activeBookingWindow && (
            <Badge variant="outline" className="mt-2">
              Active Window: {activeBookingWindow.name}
            </Badge>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Step {currentStep} of 4</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          {/* Step 1: Campaign Information */}
          {currentStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Campaign Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="campaign_name">Campaign Name *</Label>
                  <Input
                    id="campaign_name"
                    {...form.register('campaign_name')}
                    placeholder="e.g., Summer Product Launch 2026"
                  />
                  {form.formState.errors.campaign_name && (
                    <p className="text-red-500 text-sm mt-1">
                      {form.formState.errors.campaign_name.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="campaign_start_date">Campaign Start Date *</Label>
                    <Input
                      id="campaign_start_date"
                      type="date"
                      {...form.register('campaign_start_date')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="campaign_end_date">Campaign End Date *</Label>
                    <Input
                      id="campaign_end_date"
                      type="date"
                      {...form.register('campaign_end_date')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget_usd">Total Budget (USD) *</Label>
                    <Input
                      id="budget_usd"
                      type="number"
                      min="1000"
                      step="1000"
                      {...form.register('budget_usd', { valueAsNumber: true })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="target_ecpm">Target eCPM (USD) *</Label>
                    <Input
                      id="target_ecpm"
                      type="number"
                      min="0.1"
                      step="0.1"
                      {...form.register('target_ecpm', { valueAsNumber: true })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Target Demographics */}
          {currentStep === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Target Demographics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {demographics.map((demo) => (
                    <div key={demo.id} className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50">
                      <Checkbox
                        id={demo.id}
                        checked={form.watch('target_demographics')?.includes(demo.id)}
                        onCheckedChange={(checked) => 
                          handleArrayFieldChange('target_demographics', demo.id, checked as boolean)
                        }
                      />
                      <div className="flex-1">
                        <Label htmlFor={demo.id} className="font-medium cursor-pointer">
                          {demo.label}
                        </Label>
                        <p className="text-sm text-gray-600">{demo.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {form.formState.errors.target_demographics && (
                  <p className="text-red-500 text-sm mt-2">
                    {form.formState.errors.target_demographics.message}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 3: Content Preferences */}
          {currentStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Film className="h-5 w-5" />
                  Content Preferences
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {contentPreferences.map((pref) => (
                    <div key={pref.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                      <Checkbox
                        id={pref.id}
                        checked={form.watch('content_preferences')?.includes(pref.id)}
                        onCheckedChange={(checked) => 
                          handleArrayFieldChange('content_preferences', pref.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={pref.id} className="flex items-center gap-2 cursor-pointer">
                        <span className="text-lg">{pref.icon}</span>
                        <span className="text-sm font-medium">{pref.label}</span>
                      </Label>
                    </div>
                  ))}
                </div>
                {form.formState.errors.content_preferences && (
                  <p className="text-red-500 text-sm mt-2">
                    {form.formState.errors.content_preferences.message}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Success Metrics & Brand Safety */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Success Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {successMetrics.map((metric) => (
                      <div key={metric.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <Checkbox
                          id={metric.id}
                          checked={form.watch('success_metrics')?.includes(metric.id)}
                          onCheckedChange={(checked) => 
                            handleArrayFieldChange('success_metrics', metric.id, checked as boolean)
                          }
                        />
                        <Label htmlFor={metric.id} className="cursor-pointer">
                          {metric.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  {form.formState.errors.success_metrics && (
                    <p className="text-red-500 text-sm mt-2">
                      {form.formState.errors.success_metrics.message}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Brand Safety (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Note: These filters will be replaced by TV Rankings in the final system.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {suitabilityOptions.map((option) => (
                      <div key={option.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                        <Checkbox
                          id={option.id}
                          checked={form.watch('suitability_requirements')?.includes(option.id)}
                          onCheckedChange={(checked) => 
                            handleArrayFieldChange('suitability_requirements', option.id, checked as boolean)
                          }
                        />
                        <Label htmlFor={option.id} className="cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>

            {currentStep < 4 ? (
              <Button type="button" onClick={nextStep}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating Campaign...' : 'Create Campaign'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignCreationPage;